import * as yup from "yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { IoSaveOutline } from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";
import accountService from "./../../../../../services/accountService";
import Textbox from "./../../../../../components/common/Textbox";
import ReactSelect from "./../../../../../components/common/ReactSelect";
import Toaster from "./../../../../../components/common/Toaster";
import Datepicker from "./../../../../../components/common/Datepicker";
import moment from "moment";

const schema = yup.object({
  id: yup.string(),
  first_name: yup.string().required("Please enter first name."),
  last_name: yup.string().required("Please enter last name."),
  birthdate: yup.string().required("Please enter birthdate."),
  birthplace: yup.string().required("Please enter birthplace.").nullable(),
  middle_name: yup.string().required("Please enter middle name.").nullable(),
  gender: yup.object().shape({
    label: yup.string().required("Please gender."),
    value: yup.string().required("Please gender."),
  }),
  civil_status: yup.object().shape({
    label: yup.string().required("Please select type."),
    value: yup.string().required("Please select type."),
  }),
  citizenship: yup.object().shape({
    label: yup.string().required("Please select nationality."),
    value: yup.string().required("Please select nationality."),
  }),
});

const PersonalForm = ({ data, isLoading, setLoading, onSuccess }) => {
  const [genders, setGenders] = useState([]);
  const [citizenships, setCitizenships] = useState([]);
  const [civilStatuses, setCivilStatuses] = useState([]);
  const [birthdate, setBirthdate] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await accountService.saveAccountPersonalInfoAsync(data);
      Toaster.Success("Account Saved Successfully");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchGenders = () => {
      const data = accountService.getAccountGenders();
      setGenders(data);
    };

    fetchGenders();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchCitizenships = async () => {
      const data = await accountService.getAccountCitizenshipsAsync();
      const options = data.map((item) => {
        const option = {};
        option["label"] = item;
        option["value"] = item;

        return option;
      });

      if (mounted) setCitizenships(options);
    };

    fetchCitizenships();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchCivilStatuses = () => {
      const civil_statuses = accountService.getAccountCivilStatuses();
      setCivilStatuses(civil_statuses);
    };

    fetchCivilStatuses();
  }, []);

  useEffect(() => {
    const populateForm = () => {
      setValue("id", data.id);
      setValue("first_name", data.first_name);
      setValue("middle_name", data.middle_name);
      setValue("last_name", data.last_name);
      setValue("birthplace", data.birthplace);

      if (data.gender) {
        const selected_gender = {
          label: data.gender_type,
          value: data.gender,
        };

        setValue("gender", selected_gender);
      }

      if (data.civil_status) {
        const selected_civil_status = {
          label: data.civil_status_type,
          value: data.civil_status,
        };

        setValue("civil_status", selected_civil_status);
      }

      if (data.citizenship) {
        const selected_citizenship = {
          label: data.citizenship,
          value: data.citizenship,
        };

        setValue("citizenship", selected_citizenship);
      }

      if (data.birthdate) {
        const formatted_date = moment(data.birthdate).format("MM-DD-yyyy");
        setValue("birthdate", formatted_date);
        setBirthdate(moment(data.birthdate));
      }
    };

    if (data) populateForm();
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-2">
        <div className="col-span-2">
          <Textbox
            id="first-name-textbox"
            name="first_name"
            type="text"
            title="First Name"
            placeholder="e.g. Juan"
            register={register("first_name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <Textbox
            id="middle-name-textbox"
            name="middle_name"
            type="text"
            title="Middle Name"
            register={register("middle_name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <Textbox
            id="last-name-textbox"
            name="last_name"
            type="text"
            title="Last Name"
            placeholder="e.g. Dela Cruz"
            register={register("last_name")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <ReactSelect
            id="gender-dropdown"
            name="gender"
            title="Gender"
            options={genders}
            control={control}
            register={register("gender")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <ReactSelect
            id="civil-status-dropdown"
            name="civil_status"
            title="Civil Status"
            options={civilStatuses}
            control={control}
            register={register("civil_status")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <ReactSelect
            id="citizenship-dropdown"
            name="citizenship"
            title="Nationality"
            options={citizenships}
            control={control}
            register={register("citizenship")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <Datepicker
            id="birthdate-datepicker"
            name="birthdate"
            title="Birthdate"
            register={register("birthdate")}
            errors={errors}
            value={birthdate}
            setValue={setValue}
            clearErrors={clearErrors}
            placeholder="MM-DD-YYYY"
            dateFormat="MM-DD-yyyy"
            onDateChange={(date) => setBirthdate(date)}
            timeFormat={false}
            readOnly
          />
        </div>
      </div>
      <div className="grid grid-2">
        <div className="col-span-2">
          <Textbox
            id="birthplace-textbox"
            name="birthplace"
            type="text"
            title="Birthplace"
            register={register("birthplace")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default PersonalForm;
